import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
} from "@mui/material";
import { ActionModal, NoticeboardButton, ACTION_MODAL_FOOTER_PRIMARY_SIZE } from "../../shared";
import type { ChildStatus, ParentChild } from "../../../types/parentFamily";
import { FAMILY_OUTLINED_FIELD_SX } from "./familyStyles";

export interface ChildFormValues {
  id?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  grade: string;
  classSection: string;
  studentId: string;
  notes: string;
  status: ChildStatus;
}

interface ChildFormModalProps {
  open: boolean;
  onClose: () => void;
  initialChild?: ParentChild | null;
  onSave: (values: ChildFormValues) => void;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

interface DobParts {
  day: string;
  month: string; // "1".."12"
  year: string;
}

const emptyDob: DobParts = { day: "", month: "", year: "" };

/** Split a stored yyyy-mm-dd string into Day / Month / Year fields. */
const parseDob = (value?: string): DobParts => {
  if (!value) return emptyDob;
  const [year, month, day] = value.split("-");
  return {
    day: day ? String(Number(day)) : "",
    month: month ? String(Number(month)) : "",
    year: year || "",
  };
};

/** Combine Day / Month / Year into yyyy-mm-dd, or "" if incomplete/invalid. */
const combineDob = ({ day, month, year }: DobParts): string => {
  if (!day || !month || !year) return "";
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  if (![d, m, y].every(Number.isFinite)) return "";
  if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return "";
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(
    d
  ).padStart(2, "0")}`;
};

const emptyForm = (): ChildFormValues => ({
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  gender: "",
  grade: "",
  classSection: "",
  studentId: "",
  notes: "",
  status: "active",
});

export const ChildFormModal: React.FC<ChildFormModalProps> = ({
  open,
  onClose,
  initialChild,
  onSave,
}) => {
  const [form, setForm] = useState<ChildFormValues>(emptyForm());
  const [dob, setDob] = useState<DobParts>(emptyDob);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (initialChild) {
      setForm({
        id: initialChild.id,
        firstName: initialChild.firstName,
        lastName: initialChild.lastName,
        dateOfBirth: initialChild.dateOfBirth || "",
        gender: initialChild.gender || "",
        grade: initialChild.grade || "",
        classSection: initialChild.classSection || "",
        studentId: initialChild.studentId || "",
        notes: initialChild.notes || "",
        status: initialChild.status,
      });
      setDob(parseDob(initialChild.dateOfBirth));
    } else {
      setForm(emptyForm());
      setDob(emptyDob);
    }
    setError(null);
  }, [open, initialChild]);

  const updateDob = (part: keyof DobParts, value: string) => {
    const digitsOnly = value.replace(/\D/g, "");
    const next = { ...dob, [part]: digitsOnly };
    setDob(next);
    setForm((f) => ({ ...f, dateOfBirth: combineDob(next) }));
  };

  const handleSubmit = () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError("First name and surname are required.");
      return;
    }
    onSave(form);
    onClose();
  };

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title={initialChild ? "Edit Family Member" : "Add Family Member"}
      subtitle={
        initialChild
          ? "Update this family member's details."
          : "Add a child or dependent linked to this parent. You can manage fees, circles, and activities from their profile."
      }
      maxWidth="sm"
      footer={
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
          <NoticeboardButton variant="secondary" size={ACTION_MODAL_FOOTER_PRIMARY_SIZE} onClick={onClose}>
            Cancel
          </NoticeboardButton>
          <NoticeboardButton variant="primary" size={ACTION_MODAL_FOOTER_PRIMARY_SIZE} onClick={handleSubmit}>
            {initialChild ? "Save changes" : "Add Family Member"}
          </NoticeboardButton>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, ...FAMILY_OUTLINED_FIELD_SX }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <TextField
            label="First name"
            required
            value={form.firstName}
            onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
            fullWidth
            size="small"
          />
          <TextField
            label="Surname"
            required
            value={form.lastName}
            onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
            fullWidth
            size="small"
          />
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "text.secondary", mb: 1 }}
          >
            Date of birth
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 1fr", gap: 2 }}>
            <TextField
              label="Day"
              value={dob.day}
              onChange={(e) => updateDob("day", e.target.value)}
              inputProps={{ inputMode: "numeric", maxLength: 2 }}
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Month</InputLabel>
              <Select
                label="Month"
                value={dob.month}
                onChange={(e) => updateDob("month", e.target.value)}
              >
                {MONTHS.map((name, index) => (
                  <MenuItem key={name} value={String(index + 1)}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Year"
              value={dob.year}
              onChange={(e) => updateDob("year", e.target.value)}
              inputProps={{ inputMode: "numeric", maxLength: 4 }}
              fullWidth
              size="small"
            />
          </Box>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Gender</InputLabel>
            <Select
              label="Gender"
              value={form.gender}
              onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
            >
              <MenuItem value="">Not specified</MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Grade"
            value={form.grade}
            onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}
            placeholder="e.g. Grade 3"
            fullWidth
            size="small"
          />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <TextField
            label="Class"
            value={form.classSection}
            onChange={(e) => setForm((f) => ({ ...f, classSection: e.target.value }))}
            placeholder="e.g. 3A"
            fullWidth
            size="small"
          />
          <TextField
            label="Student ID"
            value={form.studentId}
            onChange={(e) => setForm((f) => ({ ...f, studentId: e.target.value }))}
            placeholder="e.g. SCH-2041"
            fullWidth
            size="small"
          />
        </Box>
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={form.status}
            onChange={(e) =>
              setForm((f) => ({ ...f, status: e.target.value as ChildStatus }))
            }
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Notes"
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          placeholder="Allergies, pickup details, or other notes for staff"
          fullWidth
          size="small"
          multiline
          minRows={2}
        />
        {error ? (
          <Box component="span" sx={{ color: "error.main", fontSize: "0.875rem" }}>
            {error}
          </Box>
        ) : null}
      </Box>
    </ActionModal>
  );
};
